<template>
  <div class="hall-page">
    <div class="bg">
      <div class="hero-inner">
        <div class="hero-ctas">
          <template v-if="businessPilotEnabled">
            <button type="button" class="hero-cta" @click="$router.push('/hall/progress')">我的交易跟进</button>
            <button type="button" class="hero-cta" @click="$router.push('/datasets/verification')">运营供给核验</button>
          </template>
          <button
            type="button"
            class="hero-cta"
            @click="goApply('demand')"
          >
            <svg class="hero-cta-icon" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="11" cy="11" r="6.2" fill="none" stroke="currentColor" stroke-width="2"/>
              <path d="M16.2 16.2 21 21" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            我要找数据
          </button>
          <button
            type="button"
            class="hero-cta"
            @click="goApply('provider')"
          >
            <svg class="hero-cta-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7 7h9.2M16.2 7 13.5 4.4M16.2 7 13.5 9.6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M17 17H7.8M7.8 17 10.5 14.4M7.8 17 10.5 19.6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            我要挂牌
          </button>
        </div>
      </div>
    </div>

    <div class="wp75 hall-stage">
      <div class="search-form">
        <div class="filter-line is-primary">
          <span class="filter-label">数据产品</span>
          <div class="filter-row">
            <span class="filter-chip" :class="{ 'is-active': !queryFrom.interfaceInformation }" @click="clearFilter('interfaceInformation')">全部</span>
            <span
              v-for="item in dataProductList"
              :key="item.value"
              class="filter-chip"
              :class="{ 'is-active': queryFrom.interfaceInformation === item.value }"
              @click="setFilter('interfaceInformation', item.value)"
            >{{ item.label }}</span>
          </div>
        </div>
        <button
          v-if="isNarrow"
          type="button"
          class="hall-filter-toggle"
          @click="filtersOpen = !filtersOpen"
        >{{ filtersOpen ? '收起筛选' : '更多筛选' }}</button>
        <div class="hall-filters-more" :class="{ 'is-collapsed': isNarrow && !filtersOpen }">
        <div class="filter-line">
          <span class="filter-label">登记类型</span>
          <div class="filter-row">
            <span class="filter-chip" :class="{ 'is-active': !queryFrom.registrationDataType }" @click="clearFilter('registrationDataType')">全部</span>
            <span
              v-for="item in registrationDataList"
              :key="item.value"
              class="filter-chip"
              :class="{ 'is-active': queryFrom.registrationDataType === item.value }"
              @click="setFilter('registrationDataType', item.value)"
            >{{ item.label }}</span>
          </div>
        </div>
        <div class="filter-line filter-line-controls">
          <div class="filter-group">
            <span class="filter-label">地区选择</span>
            <GsRegionSelect
              :value="regionPath"
              placeholder="请选择地区"
              @change="onRegionChange"
            />
          </div>
          <div class="filter-group">
            <span class="filter-label">发布时间</span>
            <el-date-picker
              :model-value="dateRange"
              type="daterange"
              class="filter-daterange"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              range-separator="至"
              value-format="YYYY-MM-DD"
              unlink-panels
              @update:model-value="onDateRangeChange"
            />
          </div>
          <div class="filter-group">
            <span class="filter-label">数据场景</span>
            <GsSelect
              class="filter-mini"
              :value="queryFrom.scenarioParent"
              placeholder="请选择"
              :options="scenarioList"
              @input="onScenarioParentChange"
            />
            <GsSelect
              class="filter-mini"
              :value="queryFrom.scenarioChild"
              :placeholder="scenarioChildList.length ? '其它信息' : '暂无子类'"
              :disabled="!scenarioChildList.length"
              :options="scenarioChildList"
              @input="onScenarioChildChange"
            />
          </div>
        </div>
        <div class="filter-line">
          <span class="filter-label">热门标签</span>
          <div class="filter-row">
            <span
              v-for="item in visibleHotTags"
              :key="'tag-' + item.value"
              class="filter-chip filter-tag"
              :class="{ 'is-active': queryFrom.scenarioParent === item.value && !queryFrom.scenarioChild }"
              @click="setHotTag(queryFrom.scenarioParent === item.value ? '' : item.value)"
            >{{ item.label }}</span>
          </div>
          <button v-if="scenarioList.length > 8" type="button" class="filter-expand" @click="tagsExpanded = !tagsExpanded">
            {{ tagsExpanded ? '收起' : '展开' }}
          </button>
        </div>
        </div>
      </div>

      <div v-if="dataChangeList && dataChangeList.length" class="table-class">
        <div
          v-for="item in dataChangeList"
          :key="item.id || item.registrationDataName || item.enterpriseName"
          class="data-card"
        >
          <div class="data-title">
            <div class="data-name">{{ cardTitle(item) }}</div>
            <div class="data-type" :class="{ 'is-wait': !hasBuyer(item) }">{{ stageLabel(item) }}</div>
          </div>
          <div class="data-detail">
            <div class="detail">
              <span class="detail-target">标的：{{ cardTarget(item) }}</span>
              <span class="detail-amount">金额：{{ cardAmount(item) }}</span>
            </div>
            <button type="button" class="detail-link" @click="detailRow(item)">
              {{ item.localBusinessId ? '查看供给' : '查看交易' }}
              <span aria-hidden="true">››</span>
            </button>
          </div>
          <div class="data-footer">
            <div class="footer">
              <i class="crile" />
              <span class="opa6 footer-label">数据交易所：</span>
              <span class="ellipsis-one opa6 w-name" :title="cardExchange(item)">{{ cardExchange(item) }}</span>
            </div>
            <div class="footer">
              <i class="crile" />
              <span class="opa6 footer-label">交易双方：</span>
              <span class="footer-num ellipsis-one" :title="cardParty(item)">{{ cardParty(item) }}</span>
            </div>
          </div>
        </div>
      </div>
      <NoData class="mt10" v-else />
      <div class="table-pagination mb20">
        <GsPagination
          :current-page="currentPage"
          :page-size="pageSize"
          :total="total"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { getTradeList, applicationScenario } from '@/api/api'
import { businessPilotEnabled } from '@/config/business'
import { enumType } from '@/utils/enum.js'
import { getDataType } from '@/api/Layout/index.js'
import NoData from '@/components/NoData/index.vue'
import GsPagination from '@/components/GsPagination/index.vue'
import GsRegionSelect from '@/components/GsRegionSelect/index.vue'
import { P } from '@/router/paths'
import { tradeProgressValue } from '@/utils/tradeProgress'

export default {
  name: 'DataChangeList',
  components: { NoData, GsPagination, GsRegionSelect },
  data() {
    return {
      businessPilotEnabled,
      total: 0,
      currentPage: 1,
      pageSize: 32,
      dataChangeList: [],
      registrationDataList: [],
      dataProductList: [],
      areaList: [],
      scenarioList: [],
      scenarioChildList: [],
      regionPath: [],
      tagsExpanded: false,
      filtersOpen: false,
      isNarrow: typeof window !== 'undefined' && window.innerWidth < 960,
      queryFrom: {
        interfaceInformation: '',
        registrationDataType: '',
        creationTimeStart: '',
        creationTimeEnd: '',
        registeredAddress: '',
        scenarioParent: '',
        scenarioChild: '',
        hotTag: ''
      }
    }
  },
  computed: {
    dateRange() {
      if (this.queryFrom.creationTimeStart && this.queryFrom.creationTimeEnd) {
        return [this.queryFrom.creationTimeStart, this.queryFrom.creationTimeEnd]
      }
      return []
    },
    visibleHotTags() {
      if (this.tagsExpanded || this.scenarioList.length <= 8) return this.scenarioList
      return this.scenarioList.slice(0, 8)
    },
    transFormEnum() {
      return function (enumList, value, multi) {
        if (!value) return ''
        let valObj = []
        if (multi) {
          valObj = JSON.parse(value)
        } else {
          valObj = String(value).split(',').map((item) => item.trim())
        }
        const labelListEnum = []
        if (enumList && enumList.length > 0) {
          enumList.forEach((item) => {
            valObj && valObj.forEach((itemObj) => {
              if (itemObj === item.value) labelListEnum.push(item.label)
            })
          })
        }
        return labelListEnum.join(',')
      }
    }
  },
  created() {
    this.loadEnterpriseList()
    this.getData('sys_enterprise_register_data_type')
    this.getData('sys_enterprise_data_product')
    this.applicationScenarioGet()
  },
  mounted() {
    window.scrollTo(0, 0)
    this.syncNarrow()
    window.addEventListener('resize', this.syncNarrow)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.syncNarrow)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.syncNarrow)
  },
  methods: {
    syncNarrow() {
      this.isNarrow = typeof window !== 'undefined' && window.innerWidth < 960
      if (!this.isNarrow) this.filtersOpen = false
    },
    /** 大厅两个主入口：找数据 → 提交需求；挂牌 → 数据服务登记 */
    goApply(kind) {
      const target = businessPilotEnabled ? (kind === 'provider' ? '/datasets/register' : '/demand/assistant') : (kind === 'provider' ? P.providerApply : P.demandApply)
      this.$router.push(target).catch(() => false)
    },
    applicationScenarioGet() {
      applicationScenario().then((response) => {
        this.scenarioList = this.normalizeScenarioList(response && response.data)
        const parent = this.scenarioList.find((item) => item.value === this.queryFrom.scenarioParent)
        this.scenarioChildList = parent && parent.children ? parent.children : []
      }).catch(() => {
        this.scenarioList = []
        this.scenarioChildList = []
      })
    },
    normalizeScenarioList(list) {
      const raw = Array.isArray(list) ? list : []
      const mapped = raw.map((item) => {
        const value = item.value ?? item.dictValue ?? item.id
        const label = item.label ?? item.dictLabel ?? item.name ?? String(value ?? '')
        const children = this.normalizeScenarioList(item.children || item.childList || item.child || [])
        return {
          ...item,
          id: item.id ?? value,
          pid: String(item.pid ?? item.parentId ?? '0'),
          value: value == null ? '' : String(value),
          label,
          children
        }
      })
      if (mapped.some((item) => item.children.length)) {
        return mapped.filter((item) => item.pid === '0' || item.pid === '' || item.pid === 'null')
      }
      const byId = new Map()
      mapped.forEach((item) => byId.set(String(item.id), item))
      const roots = []
      mapped.forEach((item) => {
        const parent = byId.get(String(item.pid))
        if (parent && String(parent.id) !== String(item.id)) parent.children.push(item)
        else roots.push(item)
      })
      return roots
    },
    childrenOf(value) {
      const parent = this.scenarioList.find((item) => item.value === value)
      return parent && Array.isArray(parent.children) ? parent.children : []
    },
    applyFilter() {
      this.currentPage = 1
      this.loadEnterpriseList()
    },
    setFilter(key, value) {
      this.queryFrom[key] = value || ''
      this.applyFilter()
    },
    clearFilter(key) {
      this.queryFrom[key] = ''
      this.applyFilter()
    },
    onDateRangeChange(value) {
      const range = Array.isArray(value) ? value : []
      this.queryFrom.creationTimeStart = range[0] || ''
      this.queryFrom.creationTimeEnd = range[1] || ''
      this.applyFilter()
    },
    onRegionChange(data) {
      this.regionPath = data || []
      this.queryFrom.registeredAddress = data && data.length ? JSON.stringify(data) : ''
      this.applyFilter()
    },
    onScenarioParentChange(value) {
      this.queryFrom.scenarioParent = value || ''
      this.queryFrom.scenarioChild = ''
      this.scenarioChildList = this.childrenOf(this.queryFrom.scenarioParent)
      this.applyFilter()
    },
    onScenarioChildChange(value) {
      this.queryFrom.scenarioChild = value || ''
      this.applyFilter()
    },
    setHotTag(value) {
      this.queryFrom.scenarioParent = value || ''
      this.queryFrom.scenarioChild = ''
      this.scenarioChildList = this.childrenOf(this.queryFrom.scenarioParent)
      this.applyFilter()
    },
    async getData(type) {
      try {
        const res = await getDataType(type)
        if (res && res.data && res.data.length > 0) {
          if (type === 'sys_enterprise_register_data_type') this.registrationDataList = enumType(res.data)
          if (type === 'sys_enterprise_data_product') this.dataProductList = enumType(res.data)
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    },
    cardTitle(item) {
      return item.tradeName || item.datasetName || '数据交易'
    },
    /** 标的：被交易的数据集；没有关联数据集时回落到卖方入驻名 */
    cardTarget(item) {
      return item.datasetName || '—'
    },
    cardAmount(item) {
      return item.transactionAmount || item.priceDescription || '面议'
    },
    hasBuyer(item) {
      return !!(item && (item.buyListingId || item.buyListingName))
    },
    /** 角标：有买方显示阶段，没有买方显示待撮合 */
    stageLabel(item) {
      return this.hasBuyer(item) ? `阶段 ${tradeProgressValue(item.transactionProgress)}/6` : '待撮合'
    },
    loadEnterpriseList() {
      getTradeList({
        datasetId: this.$route.query.dataset || null,
        pageNum: this.currentPage,
        pageSize: this.pageSize,
        whetherPublish: '1',
        registrationDataType: this.queryFrom.registrationDataType || null,
        interfaceInformation: this.queryFrom.interfaceInformation || null,
        startTime: this.queryFrom.creationTimeStart || null,
        endTime: this.queryFrom.creationTimeEnd || null,
        registeredAddress: this.queryFrom.registeredAddress || null,
        applicationScenario: this.queryFrom.scenarioChild || this.queryFrom.scenarioParent || null,
        dataApplicationScenarios: this.queryFrom.hotTag || null
      }).then((response) => {
        this.dataChangeList = (response && response.rows) || []
        this.total = (response && response.total) || 0
      }).catch(() => {
        this.dataChangeList = []
        this.total = 0
      })
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.loadEnterpriseList()
    },
    cardExchange(item) {
      return item.exchangeName || '—'
    },
    cardParty(item) {
      const sell = item.sellListingName || '待定'
      const buy = this.hasBuyer(item) ? (item.buyListingName || '待定') : '待撮合'
      return `${sell} → ${buy}`
    },
    detailRow(row) {
      if (row.localBusinessId) return this.$router.push(P.datasetDetail(row.localBusinessId))
      this.$router.push(P.hallDetail(row.id))
    }
  }
}
</script>

<style scoped lang="css">
.hall-page {
  width: 100%;
  overflow: visible;
}
.hall-page .wp75 {
  width: 100%;
  max-width: var(--gs-main-width, 1250px);
  margin-left: auto;
  margin-right: auto;
  padding: 0;
  box-sizing: border-box;
}
.bg {
  position: relative;
  background-image: url('@/assets/images/编组 28@2x.png');
  background-size: cover;
  background-position: center 28%;
  width: 100vw;
  max-width: 100vw;
  margin-left: calc(50% - 50vw);
  height: 340px;
  text-align: center;
}
.hero-inner {
  width: 100%;
  height: 100%;
}
.hero-ctas {
  position: absolute;
  left: 50%;
  top: 62%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 28px;
  z-index: 2;
}
.hero-cta {
  min-width: 168px;
  height: 46px;
  padding: 0 22px;
  border: 0;
  border-radius: 23px;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 1px;
  background: linear-gradient(90deg, #FF7A00 0%, #FFB020 100%);
  box-shadow: 0 8px 18px rgba(255, 122, 0, 0.32);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
}
.hero-cta.is-on {
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.55), 0 8px 18px rgba(255, 122, 0, 0.32);
}
.hero-cta-icon {
  width: 18px;
  height: 18px;
}
.hall-stage {
  position: relative;
  z-index: 3;
  /* 筛选卡片上浮，压在 banner 下沿 */
  margin-top: -56px;
  padding-bottom: 32px;
}
.search-form {
  width: 100%;
  box-sizing: border-box;
  background: #FFFFFF;
  border-radius: 16px;
  padding: 18px 22px 10px;
  margin: 0 0 24px;
  box-shadow: 0 14px 36px rgba(1, 101, 255, 0.16);
}
.filter-line {
  display: flex;
  align-items: center;
  gap: 10px 16px;
  padding: 7px 0;
}
.filter-label {
  flex: 0 0 72px;
  color: #333;
  font-size: 14px;
  line-height: 32px;
  white-space: nowrap;
}
.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  flex: 1;
  min-width: 0;
}
.filter-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 28px;
  padding: 0 12px;
  border-radius: 4px;
  color: #333;
  cursor: pointer;
  user-select: none;
  font-size: 14px;
  background: transparent;
}
.filter-chip.is-active {
  color: #fff;
  background: linear-gradient(180deg, #9CC8FF 0%, #4D94FF 42%, #0165FF 86%, #0050CC 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.55),
    inset 0 -1px 0 rgba(0, 50, 140, 0.22),
    0 3px 8px rgba(1, 101, 255, 0.32);
}
.filter-chip.filter-tag {
  min-width: 84px;
  height: 30px;
  border-radius: 15px;
  background: #D7E6FF;
  color: #0165FF;
}
.filter-chip.filter-tag.is-active {
  color: #fff;
  background: linear-gradient(180deg, #9CC8FF 0%, #4D94FF 42%, #0165FF 86%, #0050CC 100%);
}
.filter-line-controls {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1.1fr) minmax(0, 1.3fr);
  align-items: center;
  column-gap: 16px;
}
.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.filter-control,
.filter-daterange,
.filter-mini {
  flex: 1 1 auto;
  min-width: 0;
}
.filter-group :deep(.gs-select) {
  width: 148px;
  flex: 0 0 148px;
}
.filter-expand {
  margin-left: auto;
  border: 0;
  background: transparent;
  color: #0165FF;
  font-size: 14px;
  cursor: pointer;
}
.search-form :deep(.el-select .el-select__wrapper),
.search-form :deep(.el-date-editor),
.search-form :deep(.el-cascader) {
  min-height: 32px;
  width: 100%;
  background: #fff;
}
.table-class {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}
.data-card {
  background: #fff;
  border: 1px solid #EFEFEF;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(1, 101, 255, 0.06);
  min-height: 236px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.data-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 18px rgba(1, 101, 255, 0.16);
  border-color: #0165FF;
}
.data-title {
  background-image: url('@/assets/images/编组 15@2x.png');
  background-size: cover;
  background-position: center;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 12px;
}
.data-name {
  min-width: 0;
  font-size: 18px;
  font-weight: 700;
  color: #111824;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.data-type.is-wait {
  background: #8A9099;
}
.data-type {
  flex: 0 0 auto;
  min-width: 58px;
  height: 22px;
  padding: 0 6px;
  font-size: 11px;
  background: #D9001B;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #fff;
}
.data-detail {
  padding: 12px;
  flex: 1;
}
.footer-label {
  flex: 0 0 auto;
  white-space: nowrap;
}
.detail-target,
.detail-amount {
  display: block;
}
.detail-amount {
  color: #FF7001;
  margin-top: 2px;
}
.detail {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  font-size: 14px;
  line-height: 22px;
  color: #6b7280;
  min-height: 66px;
}
.detail-link {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  width: 100%;
  margin-top: 8px;
  border: 0;
  background: transparent;
  color: #0165FF;
  font-size: 13px;
  cursor: pointer;
}
.data-footer {
  padding: 10px 12px 12px;
  background: linear-gradient(180deg, #F7F9FD 0%, #FFFFFF 100%);
}
.footer {
  display: flex;
  align-items: center;
  min-height: 24px;
  font-size: 14px;
  color: #111824;
}
.crile {
  width: 8px;
  height: 8px;
  background: #0165FF;
  border-radius: 50%;
  margin-right: 6px;
  flex: 0 0 auto;
}
.opa6 { opacity: .7; }
.w-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.footer-num {
  margin-left: 4px;
  color: #FF7001;
  font-weight: 700;
}
.table-pagination {
  margin-top: 20px;
}
@media (max-width: 1280px) {
  .table-class { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .filter-line-controls { grid-template-columns: 1fr; }
}
@media (max-width: 1249px) {
  .hall-page .wp75 {
    padding: 0 16px;
  }
}
@media (max-width: 960px) {
  .bg { height: 200px; }
  .hall-page .wp75 {
    padding: 0 12px;
  }
  .hero-ctas { top: 58%; gap: 12px; }
  .hero-cta { min-width: 118px; height: 36px; font-size: 14px; }
  .hall-stage { margin-top: -28px; }
  .search-form { border-radius: 12px; padding: 14px 12px 8px; }
  .table-class { grid-template-columns: 1fr; }
  .search-form {
    padding: 12px;
    font-size: 13px;
  }
  .filter-label { font-size: 12px; }
  .filter-chip { height: 28px; padding: 0 10px; font-size: 12px; }
  .hall-filter-toggle {
    display: block;
    width: 100%;
    height: 32px;
    margin: 8px 0;
    border: 1px dashed #B8C9E0;
    border-radius: 6px;
    background: #fff;
    color: #0165FF;
    font-size: 13px;
    font-family: inherit;
    cursor: pointer;
  }
  .hall-filters-more.is-collapsed {
    display: none;
  }
  .data-name { font-size: 14px; }
  .detail { font-size: 12px; line-height: 20px; }
  .footer { font-size: 12px; }
}
</style>
